import React, { useState, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { users } from '@/lib/kbApi';
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, MessageSquare, Calendar, MapPin, Building } from "lucide-react";

interface EnhancedMentionProps {
  node: ProseMirrorNode;
  updateAttributes?: (attributes: Record<string, any>) => void;
  deleteNode?: () => void;
  selected?: boolean;
}

interface UserProfile {
  id: string;
  name: string;
  email: string;
  role?: string;
  department?: string;
  location?: string;
  joinDate?: string;
  lastSeen?: string;
  articlesCount?: number;
}

export function EnhancedMentionView({ node }: EnhancedMentionProps) {
  const { id, label, email } = node.attrs;
  const [showHover, setShowHover] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [hideTimeout, setHideTimeout] = useState<NodeJS.Timeout | null>(null);

  console.log('🔍 [MENTION DEBUG] EnhancedMentionView rendering with attrs:', { id, label, email });

  const handleMouseEnter = (event: React.MouseEvent<HTMLSpanElement>) => {
    const target = event.currentTarget;

    if ((window as any).__DEBUG_MENTIONS__) {
      console.log('🔍 [EDITOR] mention hover detected');
      console.log('🔍 [EDITOR] element outerHTML:', target.outerHTML);
      console.log('🔍 [EDITOR] boundingClientRect:', target.getBoundingClientRect());

      // Walk up DOM 5 levels and check computed styles
      let element: Element | null = target;
      for (let i = 0; i < 5 && element; i++) {
        const styles = window.getComputedStyle(element);
        console.log(`🔍 [EDITOR] parent level ${i}:`, {
          tagName: element.tagName,
          className: element.className,
          zIndex: styles.zIndex,
          position: styles.position,
          overflow: styles.overflow,
        });
        element = element.parentElement;
      }
    }

    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }

    const timeout = setTimeout(async () => {
      setShowHover(true);
      setLoading(true);

      if ((window as any).__DEBUG_MENTIONS__) {
        console.log('🔍 [EDITOR] setting showHover to true, creating hover card');
      }

      try {
        const response = await users.getByEmail(email || id);
        if (response.user) {
          setUserProfile(response.user);
        }
      } catch (err) {
        console.warn('Could not fetch user profile for', email || id, err);
        setUserProfile({
          id: email || id,
          name: label || (email || id).split('@')[0],
          email: email || id,
        });
      } finally {
        setLoading(false);
      }
    }, 300);

    setHoverTimeout(timeout);
  };

  const handleMouseLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }

    const timeout = setTimeout(() => {
      setShowHover(false);
      setUserProfile(null);
    }, 100);

    setHideTimeout(timeout);
  };

  const getUserInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  useEffect(() => {
    if ((window as any).__DEBUG_MENTIONS__ && showHover) {
      setTimeout(() => {
        const hoverCard = document.querySelector('.mention-hover-card');
        if (hoverCard) {
          console.log('🔍 [EDITOR] hover card mounted:', {
            element: hoverCard,
            boundingClientRect: hoverCard.getBoundingClientRect(),
            isInViewport: isInViewport(hoverCard.getBoundingClientRect()),
            computedStyles: {
              zIndex: window.getComputedStyle(hoverCard).zIndex,
              position: window.getComputedStyle(hoverCard).position,
              visibility: window.getComputedStyle(hoverCard).visibility,
              display: window.getComputedStyle(hoverCard).display,
            }
          });
        } else {
          console.log('🔍 [EDITOR] hover card NOT found in DOM');
        }
      }, 100);
    }
  }, [showHover]);

  const isInViewport = (rect: DOMRect) => {
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  useEffect(() => {
    return () => {
      if (hoverTimeout) clearTimeout(hoverTimeout);
      if (hideTimeout) clearTimeout(hideTimeout);
    };
  }, [hoverTimeout, hideTimeout]);

  return (
    <NodeViewWrapper className="inline-block relative">
      <span
        className="mention bg-primary/10 text-primary px-1 py-0.5 rounded font-medium hover:bg-primary/20 transition-colors cursor-pointer inline-block pointer-events-auto"
        data-user-id={id}
        data-user-email={email}
        data-label={label}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        @{label || id}
      </span>

      {showHover && (
        <div
          className="mention-hover-card absolute z-50 bg-background border border-border rounded-lg shadow-lg p-4 w-80 bottom-full left-0 mb-2"
          style={{ zIndex: 9999 }}
          onMouseEnter={() => {
            if (hideTimeout) {
              clearTimeout(hideTimeout);
              setHideTimeout(null);
            }
          }}
          onMouseLeave={handleMouseLeave}
        >
          {loading ? (
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-muted rounded-full animate-pulse" />
              <div className="space-y-2 flex-1">
                <div className="h-4 bg-muted rounded animate-pulse" />
                <div className="h-3 bg-muted rounded w-2/3 animate-pulse" />
              </div>
            </div>
          ) : userProfile ? (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center space-x-3">
                <Avatar className="w-12 h-12">
                  <AvatarFallback className="bg-primary/10 text-primary font-medium">
                    {getUserInitials(userProfile.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-sm truncate">
                    {userProfile.name}
                  </h4>
                  <div className="flex items-center text-xs text-muted-foreground">
                    <Mail className="w-3 h-3 mr-1" />
                    <span className="truncate">{userProfile.email}</span>
                  </div>
                </div>
              </div>

              {/* Badges */}
              <div className="flex gap-2 flex-wrap">
                {userProfile.role && (
                  <Badge variant="secondary" className="text-xs">
                    {userProfile.role}
                  </Badge>
                )}
                {userProfile.department && (
                  <Badge variant="outline" className="text-xs">
                    <Building className="w-3 h-3 mr-1" />
                    {userProfile.department}
                  </Badge>
                )}
              </div>

              {/* Details */}
              <div className="space-y-2 text-xs text-muted-foreground">
                {userProfile.location && (
                  <div className="flex items-center">
                    <MapPin className="w-3 h-3 mr-2" />
                    {userProfile.location}
                  </div>
                )}
                {userProfile.joinDate && (
                  <div className="flex items-center">
                    <Calendar className="w-3 h-3 mr-2" />
                    Joined {new Date(userProfile.joinDate).toLocaleDateString()}
                  </div>
                )}
                {userProfile.articlesCount !== undefined && (
                  <div className="flex items-center">
                    <span className="w-3 h-3 mr-2 text-center">📝</span>
                    {userProfile.articlesCount} articles authored
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigator.clipboard.writeText(userProfile.email)}
                  className="flex-1 text-xs h-8"
                >
                  <MessageSquare className="w-3 h-3 mr-1" />
                  Contact
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => window.open(`mailto:${userProfile.email}`, '_blank')}
                  className="text-xs h-8"
                >
                  <Mail className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </NodeViewWrapper>
  );
}

// For read-only mentions (in article view)
export function ReadOnlyMention({ email, name }: { email: string; name: string }) {
  return (
    <UserHoverCard
      userEmail={email}
      userName={name}
      className="mention inline-flex items-center"
    >
      <span className="mention bg-primary/10 text-primary px-1 py-0.5 rounded font-medium hover:bg-primary/20 transition-colors cursor-pointer">
        @{name}
      </span>
    </UserHoverCard>
  );
}